// Stable ordering for the gates table — rationale in design/GATE_SORTING.md.

import {
    attentionRequired,
    isGateStale,
    type GateDoc,
    type GateMode,
} from '../../types/gates';

/** Account-prefixed: one market can be gated for two accounts. */
export function observedKey(gate: GateDoc): string {
    return `${gate.account_id ?? ''}/${gate.condition_id ?? ''}`;
}

export interface SortContext {
    /** observedKey → ms, for legacy docs with no first_requested_at */
    observed: Map<string, number>;
}

export function toMs(iso: string | null | undefined): number | null {
    if (!iso) return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
}

/** Server's write-once first_requested_at, else the legacy-doc fallback. */
export function resolveSortTs(gate: GateDoc, ctx: SortContext): number {
    return (
        toMs(gate.first_requested_at) ??
        ctx.observed.get(observedKey(gate)) ??
        0
    );
}

function rank(gate: GateDoc, mode: GateMode | undefined, now: number): number {
    if (isGateStale(gate, now)) return 2;
    return attentionRequired(gate, mode) ? 0 : 1;
}

/** Nulls last; empty/undefined folded first, else null vs undefined returns 1 both ways. */
function bySlug(a: GateDoc, b: GateDoc): number {
    const x = a.market_slug || null;
    const y = b.market_slug || null;
    if (x === y) return 0;
    if (!x) return 1;
    if (!y) return -1;
    return x < y ? -1 : 1;
}

function byId(
    a: string | null | undefined,
    b: string | null | undefined
): number {
    const x = a ?? '';
    const y = b ?? '';
    return x === y ? 0 : x < y ? -1 : 1;
}

/** Total order, so no pair swaps without a real change. `now` is fixed per sort:
 *  a row straddling the staleness threshold must not flip mid-comparison. */
export function compareGates(
    ctx: SortContext,
    mode: GateMode | undefined,
    now: number = Date.now()
): (a: GateDoc, b: GateDoc) => number {
    return (a, b) =>
        rank(a, mode, now) - rank(b, mode, now) ||
        resolveSortTs(b, ctx) - resolveSortTs(a, ctx) ||
        bySlug(a, b) ||
        byId(a.condition_id, b.condition_id) ||
        byId(a.account_id, b.account_id);
}
